import { TerritoryPerformance } from '@user/store/territory/typings'

/** Compact on purpose: these sit in a three-column grid inside a panel, not in a report. */
export const money = (v: number) =>
  v >= 1000
    ? `$${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k`
    : `$${Math.round(v)}`

export type Metric = {
  label: string
  value: (p: TerritoryPerformance) => number
  format: (v: number) => string
}

/**
 * The six figures that describe an area, in the order they answer questions about it.
 *
 * Contacts first, deliberately. It is the only figure here that does not depend on the date
 * window, so it is the one to trust when a period looks quiet -- and the one that says whether
 * an area is worth owning at all.
 *
 * Shared by the draft preview panel and the summary bubble so a shape cannot be described one
 * way before it is saved and another way after: both render the same `TerritoryPerformance`,
 * and a divergence here would read as the numbers having changed on save.
 */
export const METRICS: Metric[] = [
  { label: 'Contacts', value: (p) => p.contacts, format: (v) => `${v}` },
  {
    label: 'Size',
    value: (p) => p.areaSquareMiles,
    format: (v) => `${Math.round(v)} mi²`,
  },
  { label: 'Leads', value: (p) => p.leads, format: (v) => `${v}` },
  { label: 'Visits', value: (p) => p.visits, format: (v) => `${v}` },
  { label: 'Closes', value: (p) => p.closes, format: (v) => `${v}` },
  { label: 'Won', value: (p) => p.closedValue, format: money },
]

/**
 * How many people own a territory -- or nothing, when the answer is not knowable yet.
 *
 * Zero returns the EMPTY STRING rather than "No owners", and that is the whole point. A
 * territory drawn for an assignment rule never carries owners of its own, because the rule
 * decides who gets the lead; calling it unowned names a problem that does not exist, and since
 * rules are now how territories get drawn, it is the common case rather than the edge.
 *
 * Saying the true thing means asking which rules point at the territory. The hover chip must not
 * (hovering is free, so sweeping across a dozen territories costs no requests) and the bubble has
 * not yet while its request is in flight. Both therefore say only what they know, and the bubble
 * says "No owner assigned" from its settled branch alone, once rule usage has come back empty.
 */
export const ownerCountLabel = (ownerCount: number): string =>
  ownerCount > 0 ? `${ownerCount} owner${ownerCount === 1 ? '' : 's'}` : ''
